const GOOGLE_TRANSLATE_COOKIE = 'googtrans';
const GOOGLE_TRANSLATE_SCRIPT_ID = 'google-translate-script';
const GOOGLE_TRANSLATE_CONTAINER_ID = 'google_translate_element_vibemax';
const LANGUAGE_STORAGE_KEY = 'vibemax_language';

class AutoTranslateSelector extends HTMLElement {
  static dynamicRefreshInstalled = false;

  static contentProtectionInstalled = false;

  static dynamicRefreshTimer = null;

  static dynamicRefreshAttempts = 0;

  static protectedSemanticSelector = [
    '[data-vmax-no-translate]',
    '.vmax-no-translate',
    '.vmax-online-product__eyebrow',
    '.vmax-online-product__mobile-meta > span:first-child',
    '.vmax-online-product__mobile-meta > span:last-child:not(.vmax-online-product__mobile-meta-divider)',
    '.vmax-ranking-card__eyebrow',
    '.vmax-new-drops-card__eyebrow',
    '[data-vmax-spotlight-series]',
    '[data-vmax-spotlight-character]',
    '.vmax-pdp__breadcrumbs [data-vmax-pdp-ip]',
    '.vmax-pdp__breadcrumbs [data-vmax-pdp-character]',
    '.vmax-pdp__ip',
    '.vmax-pdp__tag--character',
    '.vmax-pdp__option--character .vmax-pdp__option-text > strong',
    '[data-vmax-pdp-series]',
    '[data-vmax-pdp-character]',
    '[data-vmax-ip-name]',
    '[data-vmax-character-name]',
    '.vmax-ipinfo-detail__identity h1',
    '.vmax-ipinfo-detail__placeholder',
  ].join(',');

  connectedCallback() {
    this.trigger = this.querySelector('.auto-translate-selector__trigger');
    this.valueElement = this.querySelector('.auto-translate-selector__value');
    this.menu = this.querySelector('.auto-translate-selector__menu');
    this.options = [...this.querySelectorAll('[data-language-code]')];
    if (!(this.trigger instanceof HTMLButtonElement) || !(this.menu instanceof HTMLElement)) return;

    this.syncDefaultLanguage();
    this.setActiveLanguage(this.currentLanguage);
    this.trigger.addEventListener('click', this.toggleMenu);
    this.options.forEach((option) => option.addEventListener('click', this.onLanguageSelect));
    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener('keydown', this.onDocumentKeydown);
    AutoTranslateSelector.installContentProtection();
    AutoTranslateSelector.installDynamicTranslationRefresh();
    this.loadGoogleTranslate();
  }

  disconnectedCallback() {
    this.trigger?.removeEventListener('click', this.toggleMenu);
    this.options?.forEach((option) => option.removeEventListener('click', this.onLanguageSelect));
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('keydown', this.onDocumentKeydown);
  }

  get currentLanguage() {
    const savedLanguage = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (savedLanguage) return savedLanguage;

    const cookie = this.getCookie(GOOGLE_TRANSLATE_COOKIE);
    const targetLanguage = cookie?.split('/').filter(Boolean).pop();

    if (targetLanguage && targetLanguage !== 'auto') return targetLanguage;

    return 'en';
  }

  toggleMenu = () => {
    const isOpen = this.hasAttribute('open');
    this.setMenuOpen(!isOpen);
  };

  onDocumentClick = (event) => {
    if (event.target instanceof Node && this.contains(event.target)) return;
    this.setMenuOpen(false);
  };

  onDocumentKeydown = (event) => {
    if (event.key === 'Escape') this.setMenuOpen(false);
  };

  onLanguageSelect = (event) => {
    const option = event.currentTarget;
    if (!(option instanceof HTMLElement)) return;

    const language = option.dataset.languageCode;
    if (!language) return;

    if (language === 'en') {
      localStorage.setItem(LANGUAGE_STORAGE_KEY, 'en');
      this.clearTranslateCookie();
    } else {
      localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
      this.setTranslateCookie(`/auto/${language}`);
    }

    window.location.reload();
  };

  syncDefaultLanguage() {
    if (localStorage.getItem(LANGUAGE_STORAGE_KEY)) return;

    this.clearTranslateCookie();
  }

  setMenuOpen(isOpen) {
    this.toggleAttribute('open', isOpen);
    this.trigger?.setAttribute('aria-expanded', String(isOpen));
    if (this.menu) this.menu.hidden = !isOpen;
  }

  setActiveLanguage(language) {
    const activeLanguage = language || 'en';
    const activeOption = this.options.find((option) => option.dataset.languageCode === activeLanguage);
    const shortLabel = activeLanguage === 'zh-CN' ? '中文' : activeLanguage === 'zh-TW' ? '繁中' : activeLanguage.toUpperCase();

    if (this.valueElement) this.valueElement.textContent = shortLabel;
    this.options.forEach((option) => {
      option.toggleAttribute('aria-current', option === activeOption);
    });
  }

  loadGoogleTranslate() {
    this.ensureTranslateContainer();

    window.googleTranslateElementInit = () => {
      if (!window.google?.translate?.TranslateElement) return;

      new window.google.translate.TranslateElement(
        {
          pageLanguage: 'auto',
          includedLanguages: 'en,id,ms,th,ja,ko,tl,vi,ru,pt,zh-CN,zh-TW,fr,de,ar',
          autoDisplay: false,
        },
        GOOGLE_TRANSLATE_CONTAINER_ID
      );

      AutoTranslateSelector.scheduleDynamicTranslationRefresh(400);
    };

    if (document.getElementById(GOOGLE_TRANSLATE_SCRIPT_ID)) return;

    const script = document.createElement('script');
    script.id = GOOGLE_TRANSLATE_SCRIPT_ID;
    script.src = '//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
    script.async = true;
    document.head.appendChild(script);
  }

  ensureTranslateContainer() {
    if (document.getElementById(GOOGLE_TRANSLATE_CONTAINER_ID)) return;

    const container =
      this.querySelector('[data-google-translate-container]') || document.createElement('div');
    container.id = GOOGLE_TRANSLATE_CONTAINER_ID;
    container.hidden = true;

    if (!container.parentElement) document.body.appendChild(container);
  }

  setTranslateCookie(value) {
    const maxAge = 60 * 60 * 24 * 365;
    document.cookie = `${GOOGLE_TRANSLATE_COOKIE}=${value};path=/;max-age=${maxAge}`;
    document.cookie = `${GOOGLE_TRANSLATE_COOKIE}=${value};path=/;domain=.${window.location.hostname};max-age=${maxAge}`;
  }

  clearTranslateCookie() {
    document.cookie = `${GOOGLE_TRANSLATE_COOKIE}=;path=/;max-age=0`;
    document.cookie = `${GOOGLE_TRANSLATE_COOKIE}=;path=/;domain=.${window.location.hostname};max-age=0`;
  }

  getCookie(name) {
    return document.cookie
      .split('; ')
      .find((row) => row.startsWith(`${name}=`))
      ?.split('=')
      .slice(1)
      .join('=');
  }

  static installContentProtection() {
    this.protectContent(document);
    if (this.contentProtectionInstalled) return;
    this.contentProtectionInstalled = true;

    const observer = new MutationObserver((records) => {
      records.forEach((record) => {
        record.addedNodes.forEach((node) => {
          if (node instanceof Element) {
            this.protectContent(node);
          }
        });
      });
    });

    if (document.body) {
      observer.observe(document.body, { childList: true, subtree: true });
    }
  }

  static protectContent(root) {
    if (!(root instanceof Document) && !(root instanceof Element)) return;

    const elements = [];
    if (root instanceof Element && root.matches(this.protectedSemanticSelector)) {
      elements.push(root);
    }
    elements.push(...root.querySelectorAll(this.protectedSemanticSelector));
    elements.forEach((element) => this.markNoTranslate(element));
  }

  static markNoTranslate(element) {
    if (!(element instanceof HTMLElement) && !(element instanceof SVGElement)) return;
    element.classList.add('notranslate');
    element.setAttribute('translate', 'no');
  }

  static installDynamicTranslationRefresh() {
    if (this.dynamicRefreshInstalled) return;
    this.dynamicRefreshInstalled = true;

    const requestRefresh = () => this.scheduleDynamicTranslationRefresh();
    document.addEventListener('vmax:translatable-content-updated', requestRefresh);
    document.addEventListener('vmax:infinite-appended', requestRefresh);

    const observer = new MutationObserver((records) => {
      if (!window.matchMedia('(max-width: 749px)').matches) return;
      const hasProductCards = records.some((record) => Array.from(record.addedNodes).some((node) => {
        if (!(node instanceof Element)) return false;
        return node.matches('.vmax-online-product, [data-vmax-feed-product]')
          || Boolean(node.querySelector('.vmax-online-product, [data-vmax-feed-product]'));
      }));
      if (hasProductCards) requestRefresh();
    });

    if (document.body) {
      observer.observe(document.body, { childList: true, subtree: true });
    }
  }

  static scheduleDynamicTranslationRefresh(delay = 220) {
    if (!window.matchMedia('(max-width: 749px)').matches) return;
    window.clearTimeout(this.dynamicRefreshTimer);
    this.dynamicRefreshTimer = window.setTimeout(() => this.refreshDynamicTranslation(), delay);
  }

  static refreshDynamicTranslation() {
    const selector = document.querySelector('auto-translate-selector');
    const language = selector?.currentLanguage || 'en';
    if (language === 'en') {
      this.dynamicRefreshAttempts = 0;
      return;
    }

    const translateSelect = document.querySelector('.goog-te-combo');
    if (!(translateSelect instanceof HTMLSelectElement)) {
      if (this.dynamicRefreshAttempts >= 12) {
        this.dynamicRefreshAttempts = 0;
        return;
      }
      this.dynamicRefreshAttempts += 1;
      this.dynamicRefreshTimer = window.setTimeout(() => this.refreshDynamicTranslation(), 300);
      return;
    }

    this.dynamicRefreshAttempts = 0;
    translateSelect.value = language;
    translateSelect.dispatchEvent(new Event('change', { bubbles: true }));
  }
}

if (!customElements.get('auto-translate-selector')) {
  customElements.define('auto-translate-selector', AutoTranslateSelector);
}
